import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Save, BadgeCheck, AlertCircle } from "lucide-react";
import { fetchProfile, updateProfile } from "@lawyer/services/lawyerApi";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";

const LANGS = ["English", "Hindi", "Tamil", "Telugu", "Marathi", "Bengali", "Gujarati", "Kannada", "Malayalam", "Punjabi"];

export default function LawyerProfile() {
  const [p, setP] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try { setP(await fetchProfile()); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not load profile"); }
  };
  useEffect(() => { load(); }, []);

  const onSave = async () => {
    setBusy(true);
    try {
      const payload = {
        name: p.name,
        specialization: p.specialization,
        city: p.city,
        fee: Number(p.fee || 0),
        languages: p.languages || [],
        experience: Number(p.experience || 0),
        bio: p.bio,
        photo: p.photo,
        phone: p.phone,
      };
      const fresh = await updateProfile(payload);
      setP(fresh);
      toast.success("Profile saved");
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    setBusy(false);
  };

  if (!p) return <div className="text-slate-500" data-testid="lawyer-profile-loading">Loading…</div>;

  const toggleLang = (lng) => {
    const next = new Set(p.languages || []);
    next.has(lng) ? next.delete(lng) : next.add(lng);
    setP({ ...p, languages: Array.from(next) });
  };

  return (
    <>
      <div className="mb-6" data-testid="lawyer-profile-header">
        <h1 className="text-2xl sm:text-3xl font-extrabold text-[#0A2540]" style={{ fontFamily: "Poppins" }}>Profile</h1>
        <p className="mt-1 text-sm text-slate-500 inline-flex items-center gap-2">
          {p.verified
            ? <span className="inline-flex items-center gap-1 text-emerald-700"><BadgeCheck size={14} /> Verified by Advoset</span>
            : <span className="inline-flex items-center gap-1 text-amber-700"><AlertCircle size={14} /> Awaiting verification — clients won't see you in search yet</span>}
        </p>
      </div>

      <div className="bg-white border border-slate-200 rounded-2xl p-5 grid md:grid-cols-2 gap-4" data-testid="profile-form">
        <div>
          <Label className="text-xs">Full name</Label>
          <Input value={p.name || ""} onChange={(e) => setP({ ...p, name: e.target.value })} className="mt-1" data-testid="profile-name" />
        </div>
        <div>
          <Label className="text-xs">Specialization</Label>
          <Input value={p.specialization || ""} onChange={(e) => setP({ ...p, specialization: e.target.value })} className="mt-1" data-testid="profile-specialization" />
        </div>
        <div>
          <Label className="text-xs">City</Label>
          <Input value={p.city || ""} onChange={(e) => setP({ ...p, city: e.target.value })} className="mt-1" data-testid="profile-city" />
        </div>
        <div>
          <Label className="text-xs">Phone</Label>
          <Input value={p.phone || ""} onChange={(e) => setP({ ...p, phone: e.target.value })} className="mt-1" data-testid="profile-phone" />
        </div>
        <div>
          <Label className="text-xs">Years of experience</Label>
          <Input type="number" value={p.experience ?? ""} onChange={(e) => setP({ ...p, experience: e.target.value })} className="mt-1" data-testid="profile-experience" />
        </div>
        <div>
          <Label className="text-xs">Base fee (₹)</Label>
          <Input type="number" value={p.fee ?? ""} onChange={(e) => setP({ ...p, fee: e.target.value })} className="mt-1" data-testid="profile-fee" />
          <p className="text-[10px] text-slate-400 mt-1">Type-specific fees are configured by admin (Chat 1×, Audio 1.2×, Video 1.5×).</p>
        </div>
        <div className="md:col-span-2">
          <Label className="text-xs">Languages</Label>
          <div className="mt-1 flex flex-wrap gap-2" data-testid="profile-languages">
            {LANGS.map((l) => (
              <button
                key={l}
                onClick={() => toggleLang(l)}
                className={`px-3 py-1.5 rounded-full text-xs font-bold border transition ${
                  (p.languages || []).includes(l) ? "bg-[#0A2540] text-[#D4AF37] border-[#0A2540]" : "bg-white border-slate-200 text-slate-600 hover:border-[#0A2540]"
                }`}
              >
                {l}
              </button>
            ))}
          </div>
        </div>
        <div className="md:col-span-2">
          <Label className="text-xs">Bio (shown on your public profile)</Label>
          <textarea
            value={p.bio || ""}
            onChange={(e) => setP({ ...p, bio: e.target.value })}
            data-testid="profile-bio"
            rows={4}
            className="mt-1 w-full px-3 py-2 border border-slate-200 rounded-md text-sm outline-none focus:border-[#0A2540]"
            placeholder="Specialties, courts you practice in, notable wins…"
          />
        </div>
        <div className="md:col-span-2">
          <Label className="text-xs">Profile photo URL</Label>
          <Input value={p.photo || ""} onChange={(e) => setP({ ...p, photo: e.target.value })} className="mt-1" data-testid="profile-photo" placeholder="https://…" />
          <p className="text-[10px] text-slate-400 mt-1">Phase 2 will replace this with direct upload to S3.</p>
        </div>
      </div>

      <div className="mt-4 flex justify-end">
        <Button onClick={onSave} disabled={busy} data-testid="profile-save-btn" className="rounded-xl adv-btn-primary font-bold">
          <Save size={14} /> Save profile
        </Button>
      </div>
    </>
  );
}
